/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import { Button, Text } from 'sarsaparilla';
import cx from 'classnames';
import range from 'lodash/range';

import StickyTableHeaderHelper from 'ui-permit/shared-components/detailed-availability/StickyTableHeaderHelper';
import {
    Grid,
    Row,
    GridHeaderCell,
    GridCell,
} from 'ui-permit/src/components/detailed-availability-page-new/Grid';
import SortableTableColumnHeader from './detailed-availability-page/SortableTableColumnHeader';
import AvailabilityCell from './AvailabilityCell';
import {
    getSortedDivisions,
    getAvailability,
    getBWCAWWeekStart,
} from '../utils/permitUtilsNew';
import {
    GROUP_AVAILABILITY_KEY_NAME,
    GROUP_AVAILABILITY_WEEKLY_KEY_NAME,
} from '../constants';

const propTypes = {
    divisions: PropTypes.array,
    availabilities: PropTypes.object,
    permitDate: PropTypes.string,
    selectedStartDate: PropTypes.string,
    selectedDivision: PropTypes.object,
    onSelectToggle: PropTypes.func,
    onDivisionDetailsClick: PropTypes.func,
    isSmallerGrid: PropTypes.bool,
    isFullView: PropTypes.bool,
    numOfCols: PropTypes.number,
    children: PropTypes.node,
};

const defaultProps = {};

const SORT_COLS_DISTRICT = ['district', 'name'];
const SORT_COLS_NAME = ['name'];

export default class OvernightAvailabilityGrid extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            sort: {
                column: SORT_COLS_NAME,
                isSorted: true,
                isAsc: true,
            },
        };
        this.state.divisions = getSortedDivisions(props.divisions, this.state.sort);
        this.onColumnSortToggle = this.onColumnSortToggle.bind(this);
        this.onDistrictSortToggle = this.onDistrictSortToggle.bind(this);
        this.onNameSortToggle = this.onNameSortToggle.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.divisions !== this.props.divisions) {
            this.setState({
                divisions: getSortedDivisions(nextProps.divisions, this.state.sort),
            });
        }
    }

    onColumnSortToggle(columnName, isSorted, isAsc) {
        this.setState(
            {
                sort: {
                    column: columnName,
                    isSorted,
                    isAsc,
                },
            },
            () => {
                this.setState({
                    divisions: getSortedDivisions(this.state.divisions, this.state.sort),
                });
            }
        );
    }

    onNameSortToggle(isSorted, isAsc) {
        this.onColumnSortToggle(SORT_COLS_NAME, isSorted, isAsc);
    }

    onDistrictSortToggle(isSorted, isAsc) {
        this.onColumnSortToggle(SORT_COLS_DISTRICT, isSorted, isAsc);
    }

    onSelectToggle(division, index) {
        if (this.props.onSelectToggle) {
            if (index >= 0) {
                let permitDate = this.props.permitDate;
                if (permitDate) {
                    permitDate = gnoment(permitDate)
                        .clone()
                        .add(index, 'day')
                        .startOf('day')
                        .toISOString();
                }
                this.props.onSelectToggle(division, permitDate);
            } else {
                this.props.onSelectToggle(division);
            }
        }
    }

    getDateHeader(index) {
        const dt = gnoment(this.props.permitDate).startOf('day').add(index, 'day');
        return (
            <GridHeaderCell key={index} aria-disabled="true">
                <div>
                    <span className="rec-sr-only">{dt.format('dddd, MMMM D, YYYY')}</span>
                    <span className="weekday" aria-hidden>
                        {dt.format('ddd')}
                    </span>
                    <span className="date" aria-hidden>
                        {dt.format('D')}
                    </span>
                </div>
            </GridHeaderCell>
        );
    }

    getAvailabilityCell(division, index) {
        let isSelectedDivision = false;
        if (
            this.props.selectedDivision &&
            this.props.selectedDivision.id === division.id
        ) {
            isSelectedDivision = true;
        }
        const permitDate = gnoment(this.props.permitDate);
        const date = permitDate.clone().startOf('day').add(index, 'day');
        const isSelectedCell =
            isSelectedDivision && date.isSame(gnoment(this.props.selectedStartDate));
        let count = null;
        let total = null;
        let reserved = null;
        let dailyCount = null;
        let dailyTotal = null;
        let weeklyCount = null;
        let weeklyTotal = null;

        const avail = getAvailability(
            this.props.availabilities,
            permitDate,
            division.id,
            date,
            GROUP_AVAILABILITY_KEY_NAME
        );
        if (avail) {
            count = avail.remaining;
            total = avail.total;
            reserved = total - count;
        }
        const dailyAvail = getAvailability(
            this.props.availabilities,
            permitDate,
            division.id,
            date,
            GROUP_AVAILABILITY_KEY_NAME
        );
        if (dailyAvail) {
            dailyCount = dailyAvail.remaining;
            dailyTotal = dailyAvail.total;
        }
        const weekstart = getBWCAWWeekStart(date);
        const weeklyAvail = getAvailability(
            this.props.availabilities,
            permitDate,
            division.id,
            weekstart,
            GROUP_AVAILABILITY_WEEKLY_KEY_NAME
        );
        if (weeklyAvail) {
            weeklyCount = weeklyAvail.remaining;
            weeklyTotal = weeklyAvail.total;
            if (count && weeklyCount < count) {
                count = weeklyCount;
            }
        }

        let isSelectable = true;
        if (count === 0) {
            isSelectable = false;
        }

        // if date is before now
        if (date.clone().startOf('day').isBefore(gnoment().startOf('day'))) {
            isSelectable = false;
        }

        return (
            <AvailabilityCell
                key={index}
                dateLabel={date.format('MMMM DD')}
                count={count}
                total={total}
                reserved={reserved}
                dailyCount={dailyCount}
                dailyTotal={dailyTotal}
                weeklyCount={weeklyCount}
                weeklyTotal={weeklyTotal}
                isSelected={isSelectedCell}
                isChecked={isSelectedCell}
                isSelectable={isSelectable}
                divisionName={division.name}
                onSelectToggle={this.onSelectToggle.bind(this, division, index)}
            />
        );
    }

    renderHeader() {
        return (
            <div>
                <Row>
                    <SortableTableColumnHeader
                        id="Sites"
                        name="Sites"
                        isSorted={
                            this.state.sort.column === SORT_COLS_NAME
                                ? this.state.sort.isSorted
                                : false
                        }
                        isAsc={
                            this.state.sort.column === SORT_COLS_NAME
                                ? this.state.sort.isAsc
                                : false
                        }
                        onSortToggle={this.onNameSortToggle}
                    />
                    {!this.props.isSmallerGrid && (
                        <SortableTableColumnHeader
                            id="Area"
                            name="Area"
                            isSorted={
                                this.state.sort.column === SORT_COLS_DISTRICT
                                    ? this.state.sort.isSorted
                                    : false
                            }
                            isAsc={
                                this.state.sort.column === SORT_COLS_DISTRICT
                                    ? this.state.sort.isAsc
                                    : false
                            }
                            onSortToggle={this.onDistrictSortToggle}
                        />
                    )}
                    {range(this.props.numOfCols).map((i) => {
                        return this.getDateHeader(i);
                    })}
                </Row>
            </div>
        );
    }

    renderRow(division) {
        const { isSmallerGrid, numOfCols } = this.props;

        const divisionName = (
            <Text>
                <Button
                    appearance="link"
                    onClick={() => this.props.onDivisionDetailsClick(division.id)}
                >
                    {division.name}
                </Button>
            </Text>
        );

        const divisionComponent = !isSmallerGrid ? (
            divisionName
        ) : (
            <>
                {divisionName}
                <Text>{division.district}</Text>
            </>
        );

        return (
            <Row
                key={division.id}
                className={cx({
                    'selected-row':
                        this.props.selectedDivision &&
                        this.props.selectedDivision.id === division.id,
                })}
            >
                <GridCell>
                    <div translate="no">{divisionComponent}</div>
                </GridCell>
                {!isSmallerGrid && (
                    <GridCell>
                        <div translate="no">{division.district}</div>
                    </GridCell>
                )}
                {range(numOfCols).map((i) => this.getAvailabilityCell(division, i))}
            </Row>
        );
    }

    render() {
        const { isSmallerGrid, isFullView, children } = this.props;
        return (
            <div className="per-availability-table-container">
                <StickyTableHeaderHelper
                    scrollingContainerId="per-availability-main"
                    isFullView={isFullView}
                >
                    {children}
                    <div aria-hidden="true" className="sticky-availability-table-header">
                        <Grid
                            className={cx('detailed-availability-grid-new', {
                                'has-area-data': !isSmallerGrid,
                                'mobile-app': isSmallerGrid,
                            })}
                        >
                            {this.renderHeader()}
                        </Grid>
                    </div>
                </StickyTableHeaderHelper>
                <Grid
                    className={cx('detailed-availability-grid-new', {
                        'has-area-data': !isSmallerGrid,
                        'mobile-app': isSmallerGrid,
                    })}
                    caption={'Availability by Sites and Dates'}
                >
                    {this.renderHeader()}

                    {this.state.divisions.map((division, i) => {
                        return this.renderRow(division, i);
                    })}
                </Grid>
            </div>
        );
    }
}

OvernightAvailabilityGrid.propTypes = propTypes;
OvernightAvailabilityGrid.defaultProps = defaultProps;
